import {
  Box,
  Button,
  Flex,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Tag,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { NavigateText } from '../../../components/navigate-text/NavigateText';
import NoItemsFound from '../../../components/no-items-found/NoItemsFound';
import { ProviderName } from '../../../components/provider-name/ProviderName';
import ResourceGrid from '../../../components/resource-grid/ResourceGrid';
import {
  getSickVisits,
  SickVisitSlot,
} from '../../../services/market/getSickVisits';

interface SickVisitAvailabilityProps {
  marketId: string;
}

const SickVisitAvailability: React.FC<SickVisitAvailabilityProps> = ({
  marketId,
}) => {
  const [data, setData] = useState<SickVisitSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    getSickVisits(marketId)
      .then((res) => {
        if (isMounted && res.data) {
          setData(res.data.providerSlots || []);
        }
      })
      .catch((err) => {
        console.error('Error fetching sick visit availability:', err);
        if (isMounted)
          setError('Failed to load sick visit availability.');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [marketId]);

  // Helper to format time as "h:mm AM/PM" - displays time as received from backend
  const formatTime = (isoString: string) => {
    try {
      // Extract time directly from ISO string to preserve clinic's local time
      // Example: "2025-10-03T09:15:00-04:00" -> "09:15:00"
      const timeMatch = isoString.match(/T(\d{2}):(\d{2}):(\d{2})/);

      if (timeMatch) {
        const [, hoursStr, minutesStr] = timeMatch;
        let hours = parseInt(hoursStr, 10);
        const minutes = parseInt(minutesStr, 10);

        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        const formattedMinutes =
          minutes < 10 ? `0${minutes}` : minutes;

        return `${hours}:${formattedMinutes} ${ampm}`;
      }

      // Fallback: if regex doesn't match, try parsing as date
      const date = new Date(isoString);
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
      return `${hours}:${minutesStr} ${ampm}`;
    } catch (error) {
      console.error(
        'Error formatting time:',
        error,
        'for ISO string:',
        isoString
      );
      // Final fallback
      return 'Invalid Time';
    }
  };

  // Helper to render time tags with "+X more" popover
  const MAX_VISIBLE = 4;
  const renderTimeTags = (
    slots: { startTime: string; slotId: string }[]
  ) => {
    if (!slots?.length) return <Text color="gray.400">—</Text>;
    const visible = slots.slice(0, MAX_VISIBLE);
    const hidden = slots.slice(MAX_VISIBLE);

    return (
      <Wrap>
        {visible.map((slot, idx) => (
          <WrapItem key={slot.slotId || idx}>
            <Tag colorScheme="blue" variant="subtle">
              {formatTime(slot.startTime)}
            </Tag>
          </WrapItem>
        ))}
        {hidden.length > 0 && (
          <WrapItem>
            <Popover trigger="click" placement="bottom">
              <PopoverTrigger>
                <Button
                  size="xs"
                  variant="outline"
                >{`+${hidden.length} more`}</Button>
              </PopoverTrigger>
              <PopoverContent p={2}>
                <Wrap>
                  {hidden.map((slot, idx) => (
                    <WrapItem key={slot.slotId || idx}>
                      <Tag colorScheme="blue" variant="subtle">
                        {formatTime(slot.startTime)}
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </PopoverContent>
            </Popover>
          </WrapItem>
        )}
      </Wrap>
    );
  };

  // Columns definition for ResourceGrid
  const sickVisitColumns = [
    {
      header: 'Provider',
      accessor: 'provider',
      isCustomRender: true,
      render: (row: SickVisitSlot) => (
        <ProviderName
          provider={{
            id: row.providerId,
            firstname: row.firstName,
            lastname: row.lastName,
            customFields: {},
          }}
          mode="table"
          useNavigateTextStyle={true}
        />
      ),
      width: '180px',
    },
    {
      header: 'Practice',
      accessor: 'practice',
      isCustomRender: true,
      render: (row: SickVisitSlot) => (
        <NavigateText
          name={row.practiceName}
          id={row.practiceId}
          resource="practice"
        />
      ),
      width: '220px',
    },
    {
      header: 'Today',
      accessor: 'today',
      isCustomRender: true,
      render: (row: SickVisitSlot) => renderTimeTags(row.today_slots),
      width: '180px',
    },
    {
      header: 'Tomorrow',
      accessor: 'tomorrow',
      isCustomRender: true,
      render: (row: SickVisitSlot) =>
        renderTimeTags(row.tomorrow_slots),
      width: '180px',
    },
    {
      header: 'Day After Tomorrow',
      accessor: 'dayAfterTomorrow',
      isCustomRender: true,
      render: (row: SickVisitSlot) =>
        renderTimeTags(row.day_after_tomorrow_slots),
      width: '220px',
    },
  ];

  return (
    <Box mb="3rem" mt="1.75rem">
      {loading ? (
        <Flex align="center" justify="center" minH="120px">
          <Spinner size="lg" />
        </Flex>
      ) : error ? (
        <Text color="red.500" textAlign="center">
          {error}
        </Text>
      ) : data.length === 0 ? (
        <NoItemsFound
          mainMessage="No Sick Visits Found"
          detailMessage="There are no sick visit slots available for this market. Please try again later."
          refetch={() => {
            setLoading(true);
            setError(null);
            getSickVisits(marketId)
              .then((res) => {
                if (res.data) {
                  setData(res.data.providerSlots || []);
                }
              })
              .catch((err) => {
                console.error(
                  'Error fetching sick visit availability:',
                  err
                );
                setError('Failed to load sick visit availability.');
              })
              .finally(() => setLoading(false));
          }}
        />
      ) : (
        <ResourceGrid columns={sickVisitColumns} data={data} />
      )}
    </Box>
  );
};

export default SickVisitAvailability;
